import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { loginUser, storeSession } from "../Services/loginService";
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  Fade,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff, LockOutlined, SecurityOutlined } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { GoogleLogin } from "@react-oauth/google";
import api from "../utils/api";
import { ThemeContext } from "../utils/ThemeContext";
import IntroLoader from "../IntroLoader/IntroLoader";
import MFAVerify from "./MFAVerify";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [showRequestAccessModal, setShowRequestAccessModal] = useState(false);
  const [showIntroLoader, setShowIntroLoader] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const [forgotEmail, setForgotEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [requestName, setRequestName] = useState("");
  const [requestEmail, setRequestEmail] = useState("");
  const [requestReason, setRequestReason] = useState("");
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mfaState, setMfaState] = useState(null); // { mfaToken, emailHint, username }

  // Force password change dialog
  const [forcePasswordOpen, setForcePasswordOpen] = useState(false);
  const [fpwCurrent, setFpwCurrent] = useState("");
  const [fpwNew, setFpwNew] = useState("");
  const [fpwConfirm, setFpwConfirm] = useState("");
  const [fpwError, setFpwError] = useState("");
  const [fpwLoading, setFpwLoading] = useState(false);
  const [showFpwCurrent, setShowFpwCurrent] = useState(false);
  const [showFpwNew, setShowFpwNew] = useState(false);
  const [showFpwConfirm, setShowFpwConfirm] = useState(false);

  // MFA invitation (shown after successful forced password change)
  const [mfaInviteOpen, setMfaInviteOpen] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);

  // SSO providers
  const [ssoProviders, setSsoProviders] = useState({ google: { enabled: false }, saml: { enabled: false } });

  const navigate = useNavigate();
  const { reinitializeForUser } = useContext(ThemeContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  /* Final navigation helper (used after both normal login and post-dialog flows) */
  const doFinalNavigation = (user) => {
    if (user?.roleId === 3) {
      navigate("/home/memberspage", { replace: true });
    } else {
      navigate("/home", { replace: true });
    }
  };

  /* Navigate after a fully-resolved session is stored */
  const navigateAfterLogin = (user) => {
    reinitializeForUser();
    if (user.forcePasswordChange) {
      setPendingUser(user);
      setForcePasswordOpen(true);
      return;
    }
    if (user.mustResetPassword) {
      navigate("/home/user-profile?forceReset=true", { replace: true });
      return;
    }
    doFinalNavigation(user);
  };

  const handleLogin = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const trimmed = username.trim();
      const user = await loginUser(trimmed, password.trim());

      if (rememberMe) {
        localStorage.setItem("savedUsername", username);
      } else {
        localStorage.removeItem("savedUsername");
      }

      /* MFA required. show OTP screen (email or TOTP) */
      if (user.mfaRequired) {
        setMfaState({
          mfaToken:  user.mfaToken,
          mfaMethod: user.mfaMethod || 'email', // 'email' or 'totp'
          emailHint: user.emailHint,
          username:  trimmed,
        });
        return;
      }

      navigateAfterLogin(user);
    } catch (error) {
      setErrorMessage("Invalid username or password");
      setOpenSnackbar(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    handleLogin();
  };

  const handleForcePasswordChange = async () => {
    setFpwError("");
    if (!fpwCurrent) {
      setFpwError("Please enter your current (temporary) password.");
      return;
    }
    if (fpwNew.length < 8) {
      setFpwError("New password must be at least 8 characters.");
      return;
    }
    if (fpwNew === fpwCurrent) {
      setFpwError("New password must be different from your current password.");
      return;
    }
    if (fpwNew !== fpwConfirm) {
      setFpwError("Passwords do not match.");
      return;
    }
    setFpwLoading(true);
    try {
      await api.put("/user/change-password", {
        currentPassword: fpwCurrent,
        newPassword: fpwNew,
      });
      setForcePasswordOpen(false);
      setFpwCurrent("");
      setFpwNew("");
      setFpwConfirm("");
      setMfaInviteOpen(true);
    } catch (err) {
      setFpwError(
        err.response?.data?.message || "Failed to change password. Please try again."
      );
    } finally {
      setFpwLoading(false);
    }
  };

  const handleMfaInviteDone = (enableMfa) => {
    setMfaInviteOpen(false);
    setPendingUser(null);
    if (enableMfa) {
      navigate("/home/user-profile", { replace: true });
    } else {
      doFinalNavigation(pendingUser);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) {
      setErrorMessage("Please enter your email.");
      setOpenSnackbar(true);
      return;
    }

    try {
      await api.post("/forgot-password", { email: forgotEmail });

      setEmailSent(true);
      setTimeout(() => {
        setShowForgotPasswordModal(false);
        setEmailSent(false);
        setForgotEmail("");
      }, 3000);
    } catch (error) {
      setErrorMessage(error.message);
      setOpenSnackbar(true);
    }
  };

  const handleRequestAccess = async () => {
    if (!requestName || !requestEmail || !requestReason) {
      setErrorMessage("All fields are required.");
      setOpenSnackbar(true);
      return;
    }

    if (!/\S+@\S+\.\S+/.test(requestEmail)) {
      setErrorMessage("Please enter a valid email.");
      setOpenSnackbar(true);
      return;
    }

    setLoading(true);

    try {
      const response = await api.post("/request-access", {
        name: requestName,
        email: requestEmail,
        reason: requestReason,
      });

      if (response.status === 200) {
        setRequestSuccess(true);
        setTimeout(() => {
          setShowRequestAccessModal(false);
          setRequestSuccess(false);
          setRequestName("");
          setRequestEmail("");
          setRequestReason("");
        }, 3000);
      } else {
        throw new Error(response.data.message || "Failed to submit request.");
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "An error occurred.");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const savedUsername = localStorage.getItem("savedUsername");
    if (savedUsername) {
      setUsername(savedUsername);
      setRememberMe(true);
    }

    const timer = setTimeout(() => {
      setShowIntroLoader(false);
    }, 2200);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    api
      .get("/auth/sso/providers")
      .then((res) => setSsoProviders(res.data))
      .catch(() => {});
  }, []);

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const response = await api.post("/auth/google", {
        credential: credentialResponse.credential,
      });
      const data = response.data;
      storeSession(data, data.username || "");
      reinitializeForUser();
      if (data.roleId === 3) {
        navigate("/home/memberspage", { replace: true });
      } else {
        navigate("/home", { replace: true });
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Google sign-in failed. Please try again.");
      setOpenSnackbar(true);
    }
  };

  const dialogPaperSx = {
    borderRadius: isMobile ? "18px 18px 0 0" : "20px",
    background: "rgba(255,255,255,0.97)",
    backdropFilter: "blur(5px)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.20)",
    overflow: "hidden",
    ...(isMobile && {
      m: 0,
      width: "100%",
      maxWidth: "100%",
      position: "absolute",
      bottom: 0,
    }),
  };

  const dialogTextFieldSx = {
    mt: 1.25,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "12px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "12px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "12px",
    textTransform: "none",
    fontWeight: 600,
  };

  if (showIntroLoader) {
    return <IntroLoader message="Loading your HOA portal..." />;
  }

  /* MFA step. replaces the login card entirely */
  if (mfaState) {
    return (
      <MFAVerify
        mfaToken={mfaState.mfaToken}
        mfaMethod={mfaState.mfaMethod}
        emailHint={mfaState.emailHint}
        username={mfaState.username}
        onSuccess={(userData) => {
          setMfaState(null);
          navigateAfterLogin(userData);
        }}
        onBack={() => setMfaState(null)}
      />
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#eef2f7",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: isMobile
            ? "url('/hoa_mobile_9x16.jpg')"
            : "url('/hoa.jpg')",
          backgroundSize: "cover",
          backgroundPosition: isMobile ? "center top" : "center center",
          backgroundRepeat: "no-repeat",
          filter: isMobile ? "blur(4px)" : "blur(6px)",
          transform: "scale(1.05)",
          transformOrigin: "center",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: isMobile
            ? "linear-gradient(180deg, rgba(13,24,43,0.14), rgba(13,24,43,0.34))"
            : "linear-gradient(180deg, rgba(13,24,43,0.10), rgba(13,24,43,0.22))",
          backdropFilter: "blur(3.5px)",
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          minHeight: "100dvh",
          display: "flex",
          justifyContent: "center",
          alignItems: { xs: "flex-start", sm: "center" },
          px: { xs: 1.5, sm: 2 },
          pt: { xs: 7, sm: 3 },
          pb: { xs: 4, sm: 3 },
          boxSizing: "border-box",
        }}
      >
        <Fade in timeout={500}>
          <Box
            sx={{
              width: "100%",
              maxWidth: { xs: "360px", sm: "430px" },
              px: { xs: 2.25, sm: 4 },
              pt: { xs: 2.5, sm: 3 },
              pb: { xs: 3, sm: 6.5 },
              backgroundColor: "rgba(255,255,255,0.82)",
              borderRadius: { xs: "24px", sm: "20px" },
              boxShadow: {
                xs: "0 14px 40px rgba(0,0,0,0.22)",
                sm: "0 20px 60px rgba(0,0,0,0.30)",
              },
              textAlign: "center",
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
              boxSizing: "border-box",
              border: "1px solid rgba(255,255,255,0.38)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: { xs: 1.1, sm: 1.5 },
                mt: "0.15rem",
              }}
            >
              <Box
                component="img"
                src="/img/logo.png"
                alt="Logo"
                sx={{
                  width: { xs: "145px", sm: "220px" },
                  maxWidth: "100%",
                  height: "auto",
                  objectFit: "contain",
                }}
              />
            </Box>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#223142",
                mb: 0.4,
                fontSize: { xs: "1.2rem", sm: "1.6rem" },
                letterSpacing: "-0.02em",
              }}
            >
              Sign In
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "#6b778c",
                mb: 2,
                px: 1,
                fontSize: { xs: "0.88rem", sm: "0.95rem" },
              }}
            >
              Access your HOA portal
            </Typography>

            {/* SSO Buttons */}
            {(ssoProviders?.google?.enabled || ssoProviders?.saml?.enabled) && (
              <Box sx={{ mb: 3 }}>
                {ssoProviders?.google?.enabled && (
                  <Box sx={{ mb: 1.5, display: "flex", justifyContent: "center" }}>
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={() => {
                        setErrorMessage("Google sign-in failed. Please try again.");
                        setOpenSnackbar(true);
                      }}
                      useOneTap={false}
                      width="100%"
                    />
                  </Box>
                )}
                {ssoProviders?.saml?.enabled && (
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => { window.location.href = '/api/auth/saml/login'; }}
                    sx={{ mb: 1, borderColor: '#2f63ad', color: '#2f63ad', borderRadius: '12px', textTransform: 'none', fontWeight: 600 }}
                  >
                    Sign in with SSO
                  </Button>
                )}
                {/* Divider */}
                <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: '#e0e0e0' }} />
                  <Typography sx={{ mx: 2, color: '#6b778c', fontSize: 12 }}>or</Typography>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: '#e0e0e0' }} />
                </Box>
              </Box>
            )}

            <form method="post" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Username"
                name="username"
                autoComplete="username"
                variant="outlined"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                sx={{
                  mb: 1.1,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "rgba(255,255,255,0.97)",
                    borderRadius: "12px",
                  },
                }}
              />

              <TextField
                fullWidth
                label="Password"
                type={showPassword ? "text" : "password"}
                name="password"
                autoComplete="current-password"
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{
                  mb: 0.8,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "rgba(255,255,255,0.97)",
                    borderRadius: "12px",
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((prev) => !prev)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1.25 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Remember Me"
                  sx={{
                    m: 0,
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.92rem",
                      color: "#444",
                    },
                  }}
                />
              </Box>

              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  backgroundColor: "#2f63ad",
                  color: "white",
                  fontWeight: 700,
                  fontSize: "1rem",
                  py: 1.1,
                  borderRadius: "12px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#1f4f94",
                    boxShadow: "none",
                  },
                }}
              >
                {isSubmitting ? "Signing in..." : "Sign In"}
              </Button>
            </form>

            <Box
              sx={{
                mt: 1.6,
                display: "flex",
                justifyContent: "center",
                gap: 0.75,
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Link
                href="#"
                underline="hover"
                sx={{
                  fontSize: "0.9rem",
                  color: "#2f63ad",
                  cursor: "pointer",
                }}
                onClick={() => setShowForgotPasswordModal(true)}
              >
                Forgot your password?
              </Link>

              <Link
                href="#"
                underline="hover"
                sx={{
                  fontSize: "0.9rem",
                  color: "#2f63ad",
                  cursor: "pointer",
                }}
                onClick={() => setShowRequestAccessModal(true)}
              >
                Request Access
              </Link>
            </Box>

            <Typography
              variant="body2"
              sx={{
                mt: 2.2,
                textAlign: "center",
                fontWeight: 500,
                color: "#666",
                fontSize: "0.86rem",
              }}
            >
              © My HOA {new Date().getFullYear()}
            </Typography>
          </Box>
        </Fade>
      </Box>

      <Dialog
        open={showForgotPasswordModal}
        onClose={() => setShowForgotPasswordModal(false)}
        fullWidth
        maxWidth="xs"
        fullScreen={isMobile}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.35rem",
            pt: 3,
            pb: 1,
            color: "#1f1f1f",
          }}
        >
          Reset Password
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          {emailSent ? (
            <Box sx={{ textAlign: "center", py: 1.5 }}>
              <Typography sx={{ color: "green", fontWeight: 700, fontSize: "1rem" }}>
                ✅ Reset link sent! Check your email.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography
                sx={{
                  mt: 0.5,
                  mb: 1,
                  textAlign: "center",
                  color: "#666",
                  fontSize: "0.95rem",
                }}
              >
                Enter your email address and we’ll send you a reset link.
              </Typography>

              <TextField
                fullWidth
                label="Email"
                type="email"
                autoComplete="email"
                variant="outlined"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                sx={dialogTextFieldSx}
              />
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 1.5,
            justifyContent: "center",
            gap: 1.25,
            flexWrap: "wrap",
          }}
        >
          {!emailSent && (
            <Button onClick={handleForgotPassword} variant="contained" sx={primaryButtonSx}>
              Send Reset Link
            </Button>
          )}
          <Button
            onClick={() => setShowForgotPasswordModal(false)}
            variant="outlined"
            sx={secondaryButtonSx}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showRequestAccessModal}
        onClose={() => setShowRequestAccessModal(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.35rem",
            pt: 3,
            pb: 1,
            color: "#1f1f1f",
          }}
        >
          Request Access
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          {requestSuccess ? (
            <Box sx={{ textAlign: "center", py: 1.5 }}>
              <Typography sx={{ color: "green", fontWeight: 700, fontSize: "1rem" }}>
                ✅ Request submitted! We will contact you soon.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography
                sx={{
                  mt: 0.5,
                  mb: 1,
                  textAlign: "center",
                  color: "#666",
                  fontSize: "0.95rem",
                }}
              >
                Fill out the form below and our team will review your request.
              </Typography>

              <TextField
                fullWidth
                label="Full Name"
                variant="outlined"
                value={requestName}
                onChange={(e) => setRequestName(e.target.value)}
                sx={dialogTextFieldSx}
              />

              <TextField
                fullWidth
                label="Email"
                type="email"
                autoComplete="email"
                variant="outlined"
                value={requestEmail}
                onChange={(e) => setRequestEmail(e.target.value)}
                sx={dialogTextFieldSx}
              />

              <TextField
                fullWidth
                label="Reason"
                multiline
                rows={4}
                variant="outlined"
                value={requestReason}
                onChange={(e) => setRequestReason(e.target.value)}
                sx={dialogTextFieldSx}
              />
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 1.5,
            justifyContent: "center",
            gap: 1.25,
            flexWrap: "wrap",
          }}
        >
          {!requestSuccess && (
            <Button
              onClick={handleRequestAccess}
              variant="contained"
              disabled={loading}
              sx={primaryButtonSx}
            >
              {loading ? "Submitting..." : "Submit Request"}
            </Button>
          )}
          <Button
            onClick={() => setShowRequestAccessModal(false)}
            variant="outlined"
            sx={secondaryButtonSx}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* ── Force Password Change Dialog ──────────────────────────────────── */}
      <Dialog
        open={forcePasswordOpen}
        onClose={() => {}}
        disableEscapeKeyDown
        fullWidth
        maxWidth="xs"
        fullScreen={isMobile}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ textAlign: "center", fontWeight: 700, fontSize: "1.35rem", pt: 3, pb: 0.5, color: "#1f1f1f" }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 52, height: 52, borderRadius: "16px",
                backgroundColor: "rgba(47,99,173,0.10)",
                display: "flex", alignItems: "center", justifyContent: "center", mb: 0.5,
              }}
            >
              <LockOutlined sx={{ color: "#2f63ad", fontSize: 28 }} />
            </Box>
            Set Your Password
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Typography sx={{ textAlign: "center", color: "#6b778c", fontSize: "0.92rem", mb: 2 }}>
            For security, you must set a new password before continuing. Enter your temporary password and choose a new one.
          </Typography>

          <TextField
            fullWidth
            label="Current (Temporary) Password"
            type={showFpwCurrent ? "text" : "password"}
            variant="outlined"
            value={fpwCurrent}
            onChange={(e) => setFpwCurrent(e.target.value)}
            sx={dialogTextFieldSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowFpwCurrent((v) => !v)} edge="end">
                    {showFpwCurrent ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="New Password"
            type={showFpwNew ? "text" : "password"}
            variant="outlined"
            value={fpwNew}
            onChange={(e) => setFpwNew(e.target.value)}
            sx={dialogTextFieldSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowFpwNew((v) => !v)} edge="end">
                    {showFpwNew ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Confirm New Password"
            type={showFpwConfirm ? "text" : "password"}
            variant="outlined"
            value={fpwConfirm}
            onChange={(e) => setFpwConfirm(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleForcePasswordChange(); }}
            sx={dialogTextFieldSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowFpwConfirm((v) => !v)} edge="end">
                    {showFpwConfirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {fpwError && (
            <Alert severity="error" sx={{ mt: 1.5, borderRadius: "10px" }}>
              {fpwError}
            </Alert>
          )}

          <Typography sx={{ mt: 1.25, color: "#6b778c", fontSize: "0.8rem" }}>
            Password must be at least 8 characters.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, justifyContent: "center" }}>
          <Button
            onClick={handleForcePasswordChange}
            variant="contained"
            disabled={fpwLoading}
            fullWidth
            sx={primaryButtonSx}
          >
            {fpwLoading ? "Updating..." : "Set New Password"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── MFA Invitation Dialog ─────────────────────────────────────────── */}
      <Dialog
        open={mfaInviteOpen}
        onClose={() => handleMfaInviteDone(false)}
        fullWidth
        maxWidth="xs"
        fullScreen={isMobile}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ textAlign: "center", fontWeight: 700, fontSize: "1.35rem", pt: 3, pb: 0.5, color: "#1f1f1f" }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 52, height: 52, borderRadius: "16px",
                backgroundColor: "rgba(34,197,94,0.10)",
                display: "flex", alignItems: "center", justifyContent: "center", mb: 0.5,
              }}
            >
              <SecurityOutlined sx={{ color: "#15803d", fontSize: 28 }} />
            </Box>
            Secure Your Account
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Alert severity="success" sx={{ mb: 2, borderRadius: "10px" }}>
            Password updated successfully!
          </Alert>

          <Typography sx={{ textAlign: "center", color: "#444", fontSize: "0.95rem", mb: 1.75 }}>
            Add an extra layer of protection with{" "}
            <strong>Multi-Factor Authentication (MFA)</strong>. Each time you
            sign in, you'll receive a one-time code via email to verify it's
            really you.
          </Typography>

          <Box sx={{ backgroundColor: "rgba(47,99,173,0.05)", borderRadius: "12px", p: 1.75 }}>
            {[
              "Protects against unauthorized access",
              "Blocks stolen-password attacks",
              "Takes only seconds to verify",
            ].map((benefit) => (
              <Box key={benefit} sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.75, "&:last-child": { mb: 0 } }}>
                <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: "#2f63ad", flexShrink: 0 }} />
                <Typography sx={{ fontSize: "0.88rem", color: "#444" }}>{benefit}</Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, flexDirection: "column", gap: 1, alignItems: "stretch" }}>
          <Button
            onClick={() => handleMfaInviteDone(true)}
            variant="contained"
            fullWidth
            sx={primaryButtonSx}
          >
            Enable MFA Now
          </Button>
          <Button
            onClick={() => handleMfaInviteDone(false)}
            variant="outlined"
            fullWidth
            sx={secondaryButtonSx}
          >
            Maybe Later
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;